/**
 * Role Management — SESAAT
 * 
 * 3 Pangkat (Roles):
 * 1. ADMIN      — Full access. Boleh urus semua (users, keys, clients, config)
 * 2. MODERATOR  — Boleh akses admin panel, tapi terhad (view users, view clients sahaja)
 * 3. CLIENT     — Pengguna biasa, tiada akses admin
 */

export const ROLES = {
    ADMIN: 'admin',
    MODERATOR: 'moderator',
    CLIENT: 'client',
};

// ═══ EMAIL → ROLE MAPPING ═══
const ADMIN_EMAILS = ['masterzuheir@gmail.com'];
const MODERATOR_EMAILS = ['mohdzuheiralbakry@gmail.com'];

/**
 * Get role for a given email
 * @param {string} email 
 * @returns {'admin' | 'moderator' | 'client'}
 */
export const getUserRole = (email) => {
    if (!email) return ROLES.CLIENT;
    const lowerEmail = email.toLowerCase();
    if (ADMIN_EMAILS.includes(lowerEmail)) return ROLES.ADMIN;
    if (MODERATOR_EMAILS.includes(lowerEmail)) return ROLES.MODERATOR;
    return ROLES.CLIENT;
};

/**
 * Check if role has access to admin panel
 */
export const canAccessAdmin = (role) => {
    return role === ROLES.ADMIN || role === ROLES.MODERATOR;
};

/**
 * Permission checks — what each role can do in admin panel
 */
export const PERMISSIONS = {
    // Users tab
    VIEW_USERS: [ROLES.ADMIN, ROLES.MODERATOR],
    EDIT_USERS: [ROLES.ADMIN],

    // Generate Key tab
    GENERATE_KEYS: [ROLES.ADMIN],

    // Clients tab
    VIEW_CLIENTS: [ROLES.ADMIN, ROLES.MODERATOR],
    EDIT_CLIENTS: [ROLES.ADMIN],

    // Config tab
    VIEW_CONFIG: [ROLES.ADMIN],
    EDIT_CONFIG: [ROLES.ADMIN],

    // Automation Scripts tab
    MANAGE_AUTOMATION: [ROLES.ADMIN],

    // Error Logs tab
    VIEW_ERROR_LOGS: [ROLES.ADMIN],

    // AI Cost tab — pipeline usage tracking
    VIEW_PIPELINE_USAGE: [ROLES.ADMIN],
    EDIT_PIPELINE_PRICING: [ROLES.ADMIN],

    // Library
    VIEW_LIBRARY: [ROLES.ADMIN, ROLES.MODERATOR, ROLES.CLIENT],
    MANAGE_LIBRARY: [ROLES.ADMIN],

    // Payment Submissions (Pending Premium tab) — admin only
    MANAGE_PAYMENT_SUBMISSIONS: [ROLES.ADMIN],

    // Email Blast tab — admin only
    MANAGE_EMAIL: [ROLES.ADMIN],

    // Flow tab — admin only. SENGAJA berasingan daripada MANAGE_PAYMENT_SUBMISSIONS:
    // tab ini memegang kredential akaun Google (password + kunci 2FA), jadi ia tidak
    // boleh terbuka secara tak sengaja bila kebenaran pembayaran diluaskan nanti.
    MANAGE_FLOW_ULTRA: [ROLES.ADMIN],
};

/**
 * Check if a role has a specific permission
 * @param {string} role 
 * @param {string[]} allowedRoles 
 * @returns {boolean}
 */
export const hasPermission = (role, allowedRoles) => {
    return allowedRoles.includes(role);
};

/**
 * Role display info — label, color, icon
 */
export const ROLE_DISPLAY = {
    [ROLES.ADMIN]: {
        label: 'Admin',
        color: 'indigo',
        bgClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
        icon: 'shield',
    },
    [ROLES.MODERATOR]: {
        label: 'Moderator',
        color: 'purple',
        bgClass: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        icon: 'supervisor_account',
    },
    [ROLES.CLIENT]: {
        label: 'Client',
        color: 'slate',
        bgClass: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
        icon: 'person',
    },
};
